// B2-GATES (universal-task plan §b.4; planned-outputs §3): the board settings tab "Gates" — what a
// person wants to see before a DE moves a card here. Each gate is a move (from a status, or any,
// to a status) and a mode: Ask (the DE's move becomes a proposal to the card's owner), Hold (the
// move happens; the owner may pull it back within the window) or Trust. Mine are the viewer's
// own; Everyone is the board default (an editor sets it); an admin sees everyone's and may forbid
// Trust on the board (the company ceiling: a Trust gate then reads as Hold). From
// `GET boards/{id}/gates`; written with `PUT boards/{id}/gates`, `DELETE …/gates/{gate_id}` and
// `PUT …/gates/ceiling`. Every action is a visible hover icon; quick edits are a modal;
// explanations sit behind (i).
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Switch,
} from "@companyos/ui";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../../api/client";
import { problemText } from "../../settings/problem-text";
import type { Board } from "../view-engine";
import { workKeys } from "../work-queries";
import { type BoardSettingsTabProps, registerBoardSettingsTab } from "./registry";

export type BoardGates = OperationOutput<"boards.gates">;
type GateRow = BoardGates["gates"][number];
type Mode = GateRow["mode"];

export const gatesKey = (co: string, board: string) =>
  [...workKeys.board(co, board), "gates"] as const;

export const gatesQuery = (api: Api, co: string, board: string) =>
  queryOptions({
    queryKey: gatesKey(co, board),
    queryFn: () => api.client.boards.gates({ params: { co, id: board } }),
  });

const MODE_LABEL: Record<Mode, string> = { ask: "Ask", hold: "Hold", trust: "Trust" };
const MODE_TONE: Record<Mode, "warning" | "info" | "secondary"> = {
  ask: "warning",
  hold: "info",
  trust: "secondary",
};

const hoursOf = (min: number) =>
  min % 1440 === 0 ? `${min / 1440}d` : min % 60 === 0 ? `${min / 60}h` : `${min}m`;

const statusName = (b: Board, key: string | null) =>
  key === null ? "Any" : (b.status_set.find((s) => s.key === key)?.name ?? key);

export function GatesTab({ board, can, api, co }: BoardSettingsTabProps) {
  const q = useQuery({
    ...gatesQuery(api as Api, co ?? "", board.id),
    enabled: Boolean(api && co),
  });
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ row: GateRow | null; seat: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  if (!api || !co) return null;
  const g = q.data;
  if (!g)
    return (
      <p className="text-sm text-muted-foreground">
        {q.isError ? problemText(q.error, "The gates could not be read.") : "Loading…"}
      </p>
    );
  const live = !board.archived_at;
  const mySeat = g.my_seat_ids[0] ?? null;
  const mine = g.gates.filter((r) => r.mine);
  const everyone = g.gates.filter((r) => r.seat_id === null);
  const others = g.gates.filter((r) => r.seat_id !== null && !r.mine);
  const act = async (fail: string, fn: () => Promise<unknown>) => {
    setPending(true);
    setError(null);
    try {
      const out = await fn();
      if (out && typeof out === "object" && "gates" in out)
        qc.setQueryData(gatesKey(co, board.id), out as BoardGates);
      else await qc.invalidateQueries({ queryKey: gatesKey(co, board.id) });
    } catch (err) {
      setError(problemText(err, fail));
    } finally {
      setPending(false);
    }
  };
  const remove = (r: GateRow) =>
    act("The gate could not be removed.", () =>
      api.client.boards.deleteGate({ params: { co, id: board.id, gate_id: r.id } }),
    );
  const group = (
    label: string,
    tip: string,
    rows: GateRow[],
    addFor: { seat: string | null } | null,
    empty: string,
  ) => (
    <section className="group/gates flex flex-col gap-1" aria-label={label}>
      <header className="flex items-center gap-1.5">
        <h3 className="m-0 text-sm font-medium">{label}</h3>
        <InfoTip label={`About ${label}`}>{tip}</InfoTip>
        {addFor && live ? (
          <Button
            variant="ghost"
            size="icon-xs"
            className="ml-auto opacity-0 group-hover/gates:opacity-100 group-focus-within/gates:opacity-100 pointer-coarse:opacity-100"
            aria-label={`Add a gate (${label})`}
            title="Add a gate"
            onClick={() => setEditing({ row: null, seat: addFor.seat })}
          >
            <PlusIcon aria-hidden="true" />
          </Button>
        ) : null}
      </header>
      {rows.length ? (
        <ul aria-label={`${label} gates`} className="m-0 flex list-none flex-col gap-0.5 p-0">
          {rows.map((r) => (
            <li
              key={r.id}
              data-gate={r.id}
              className="group/gate flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted"
            >
              <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
                {r.seat_id && !r.mine ? (
                  <span className="truncate text-muted-foreground">
                    {r.holder_name ?? r.seat_title ?? "Someone"}:
                  </span>
                ) : null}
                <span className="truncate">{statusName(board, r.from_status)}</span>
                <ArrowRightIcon aria-hidden="true" className="size-3.5 shrink-0" />
                <span className="truncate font-medium">{statusName(board, r.to_status)}</span>
              </span>
              <Badge variant={MODE_TONE[r.effective_mode]} data-mode={r.effective_mode}>
                {MODE_LABEL[r.effective_mode]}
                {r.effective_mode === "hold" ? ` ${hoursOf(r.hold_minutes)}` : ""}
                {r.effective_mode !== r.mode ? " (ceiling)" : ""}
              </Badge>
              {r.can_edit && live ? (
                <span className="flex shrink-0 items-center opacity-0 group-hover/gate:opacity-100 group-focus-within/gate:opacity-100 pointer-coarse:opacity-100">
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Edit the gate to ${statusName(board, r.to_status)}`}
                    title="Edit"
                    onClick={() => setEditing({ row: r, seat: r.seat_id })}
                  >
                    <PencilIcon aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Remove the gate to ${statusName(board, r.to_status)}`}
                    title="Remove"
                    disabled={pending}
                    onClick={() => void remove(r)}
                  >
                    <Trash2Icon aria-hidden="true" />
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 px-2 text-xs text-muted-foreground">{empty}</p>
      )}
    </section>
  );
  return (
    <div data-tab="gates" className="flex flex-col gap-4 text-sm">
      <p className="m-0 flex items-center gap-1.5 text-muted-foreground">
        What you see before a DE moves a card
        <InfoTip label="About gates">
          Ask: the DE's move waits for the card's owner to approve it (several at once come as one
          batch review). Hold: the move happens, and the owner gets a quiet Inbox line and may pull
          it back until the window ends. Trust: the move happens. A person's own gate wins over the
          board default. People's own moves are never gated.
        </InfoTip>
      </p>
      {g.can_set_ceiling || g.no_trust ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-subtle px-3 py-2">
          <span className="inline-flex items-center gap-1.5">
            No trust here
            <InfoTip label="About the ceiling">
              A company admin may forbid Trust on this board: every Trust gate then acts as Hold, so
              a person always has a window to pull a DE's move back.
            </InfoTip>
          </span>
          <Switch
            aria-label="No trust here"
            checked={g.no_trust}
            disabled={!g.can_set_ceiling || pending || !live}
            onCheckedChange={(v: boolean) =>
              void act("The ceiling could not be set.", () =>
                api.client.boards.gateCeiling({
                  params: { co, id: board.id },
                  body: { no_trust: v },
                }),
              )
            }
          />
        </div>
      ) : null}
      {mySeat
        ? group(
            "Mine",
            "Your own gates: they apply to the cards you own here and win over the board default.",
            mine,
            { seat: mySeat },
            "None: the board default applies to your cards.",
          )
        : null}
      {group(
        "Everyone",
        "The board default for every card owner who has no gate of their own on that move.",
        everyone,
        g.can_edit_default ? { seat: null } : null,
        "No default: a DE's moves here need no approval.",
      )}
      {others.length
        ? group("Others", "Other people's gates on this board (admins see them).", others, null, "")
        : null}
      {error ? (
        <p role="alert" className="m-0 text-xs text-danger-text">
          {error}
        </p>
      ) : null}
      {editing ? (
        <GateDialog
          board={board}
          row={editing.row}
          seat={editing.seat}
          noTrust={g.no_trust}
          onClose={() => setEditing(null)}
          onSave={(body) =>
            act("The gate could not be saved.", () =>
              api.client.boards.putGate({ params: { co, id: board.id }, body }),
            ).then(() => setEditing(null))
          }
          pending={pending}
          canEdit={editing.seat !== null || can.edit || can.manage}
        />
      ) : null}
    </div>
  );
}

type PutBody = {
  seat_id: string | null;
  from_status: string | null;
  to_status: string;
  mode: Mode;
  hold_minutes?: number;
};

function GateDialog({
  board,
  row,
  seat,
  noTrust,
  onClose,
  onSave,
  pending,
  canEdit,
}: {
  board: Board;
  row: GateRow | null;
  seat: string | null;
  noTrust: boolean;
  onClose: () => void;
  onSave: (body: PutBody) => Promise<unknown>;
  pending: boolean;
  canEdit: boolean;
}) {
  const id = useId();
  const statuses = board.status_set;
  const [from, setFrom] = useState(row?.from_status ?? "");
  const [to, setTo] = useState(row?.to_status ?? statuses[1]?.key ?? statuses[0]?.key ?? "");
  const [mode, setMode] = useState<Mode>(
    row?.mode === "trust" && noTrust ? "hold" : (row?.mode ?? "ask"),
  );
  const [hours, setHours] = useState(String((row?.hold_minutes ?? 1440) / 60));
  const h = Number(hours);
  const validHold = mode !== "hold" || (Number.isFinite(h) && h >= 0.1 && h <= 168);
  const valid = to !== "" && from !== to && validHold;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    void onSave({
      seat_id: seat,
      from_status: from === "" ? null : from,
      to_status: to,
      mode,
      ...(mode === "hold" ? { hold_minutes: Math.max(5, Math.round(h * 60)) } : {}),
    });
  };
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{row ? "Edit gate" : "Add a gate"}</DialogTitle>
            <DialogDescription>
              {seat ? "Your own gate on this board." : "The board default for every owner."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 text-sm">
            <label htmlFor={`${id}-from`} className="text-muted-foreground">
              From
            </label>
            <NativeSelect
              id={`${id}-from`}
              value={from}
              disabled={Boolean(row)}
              onChange={(e) => setFrom(e.target.value)}
            >
              <NativeSelectOption value="">Any status</NativeSelectOption>
              {statuses.map((s) => (
                <NativeSelectOption key={s.key} value={s.key}>
                  {s.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <label htmlFor={`${id}-to`} className="text-muted-foreground">
              To
            </label>
            <NativeSelect
              id={`${id}-to`}
              value={to}
              disabled={Boolean(row)}
              onChange={(e) => setTo(e.target.value)}
            >
              {statuses.map((s) => (
                <NativeSelectOption key={s.key} value={s.key}>
                  {s.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <span className="text-muted-foreground">Mode</span>
            <div role="radiogroup" aria-label="Mode" className="flex gap-1">
              {(["ask", "hold", "trust"] as const).map((m) => (
                <Button
                  key={m}
                  type="button"
                  size="sm"
                  role="radio"
                  aria-checked={mode === m}
                  variant={mode === m ? "default" : "outline"}
                  disabled={m === "trust" && noTrust}
                  title={
                    m === "trust" && noTrust ? "An admin does not allow Trust here" : undefined
                  }
                  onClick={() => setMode(m)}
                >
                  {MODE_LABEL[m]}
                </Button>
              ))}
            </div>
            {mode === "hold" ? (
              <>
                <label htmlFor={`${id}-hours`} className="text-muted-foreground">
                  Window (h)
                </label>
                <Input
                  id={`${id}-hours`}
                  inputMode="decimal"
                  className="h-8 w-24 text-right tabular-nums"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                />
              </>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || pending || !canEdit}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const off = registerBoardSettingsTab({
  id: "gates",
  title: "Gates",
  order: 25,
  Component: GatesTab,
  when: (p) => Boolean(p.api && p.co),
});
// A hot reload re-runs this module: the old registration goes first (no duplicate id).
import.meta.hot?.dispose(off);
